import React, { useState } from 'react';
import { useAuth } from '../hooks/use-auth';
import { Button } from './ui/button';

interface AuthTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthTokenModal({ isOpen, onClose }: AuthTokenModalProps) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [apiTest, setApiTest] = useState<{ loading: boolean; result?: any; error?: string }>({ loading: false });

  if (!isOpen) return null;

  const accessToken = user?.access_token;

  const copyToClipboard = async () => {
    if (accessToken) {
      try {
        await navigator.clipboard.writeText(accessToken);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy token:', err);
      }
    }
  };

  const testApiCall = async () => {
    if (!accessToken) return;
    
    setApiTest({ loading: true });
    try {
      const response = await fetch('http://localhost:5000/health', {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const result = await response.text();
        setApiTest({ loading: false, result: `✅ Success: ${result}` });
      } else {
        setApiTest({ loading: false, error: `❌ Error: ${response.status} ${response.statusText}` });
      }
    } catch (error) {
      setApiTest({ loading: false, error: `❌ Network Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
    }
  };

  const decodeTokenPayload = (token: string) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  };

  const tokenPayload = accessToken ? decodeTokenPayload(accessToken) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-secondary-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">Current User Access Token</h2>
          <button
            onClick={onClose}
            className="text-secondary-500 dark:text-secondary-400 hover:text-secondary-700 text-2xl"
          >
            ×
          </button>
        </div>

        {!accessToken ? (
          <div className="text-secondary-600 dark:text-secondary-400 text-center py-8">
            No access token available. Please sign in first.
          </div>
        ) : (
          <div className="space-y-6">
            {/* Token Claims */}
            {tokenPayload && (
              <div>
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-3">Decoded Token Claims</h3>
                <div className="bg-secondary-50 dark:bg-secondary-900 rounded-lg p-4 border">
                  <pre className="text-sm text-secondary-700 dark:text-secondary-200 whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(tokenPayload, null, 2)}
                  </pre>
                </div>
              </div>
            )}

            {/* Key Claims Summary */}
            {tokenPayload && (
              <div>
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-3">Key Claims Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                    <div className="text-sm font-medium text-blue-900">User Identity</div>
                    <div className="text-sm text-blue-700 mt-1">
                      <div>Name: {tokenPayload.given_name} {tokenPayload.family_name}</div>
                      <div>Username: {tokenPayload.preferred_username}</div>
                      <div>Email: {tokenPayload.email}</div>
                    </div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-950 rounded-lg p-3 border border-green-200 dark:border-green-800">
                    <div className="text-sm font-medium text-green-900">Authentication</div>
                    <div className="text-sm text-green-700 dark:text-green-400 mt-1">
                      <div>Audience: {tokenPayload.aud}</div>
                      <div>Client: {tokenPayload.azp}</div>
                      <div>Issuer: {tokenPayload.iss}</div>
                    </div>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-950 rounded-lg p-3 border border-yellow-200 dark:border-yellow-800">
                    <div className="text-sm font-medium text-yellow-900">Authorization</div>
                    <div className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      <div>Roles: {tokenPayload.roles ? JSON.stringify(tokenPayload.roles) : 'None'}</div>
                      <div>Divisions: {tokenPayload.divisionId ? JSON.stringify(tokenPayload.divisionId) : 'None'}</div>
                    </div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-950 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                    <div className="text-sm font-medium text-purple-900">Token Info</div>
                    <div className="text-sm text-purple-700 dark:text-purple-400 mt-1">
                      <div>Expires: {new Date(tokenPayload.exp * 1000).toLocaleString()}</div>
                      <div>Issued: {new Date(tokenPayload.iat * 1000).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Raw Token */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white">Raw Access Token</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  className={copied ? 'bg-green-50 dark:bg-green-950 border-green-300 text-green-700 dark:text-green-400' : ''}
                >
                  {copied ? 'Copied!' : 'Copy Token'}
                </Button>
              </div>
              <div className="bg-secondary-100 dark:bg-secondary-700 rounded-lg p-4 border">
                <div className="text-xs text-secondary-600 dark:text-secondary-400 font-mono break-all">
                  {accessToken}
                </div>
              </div>
            </div>

            {/* Usage Instructions */}
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-3">Usage Instructions</h3>
              <div className="bg-blue-50 dark:bg-blue-950 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
                <div className="text-sm text-blue-700">
                  <p className="mb-2">To use this token for API calls:</p>
                  <div className="bg-blue-100 dark:bg-blue-900 rounded p-2 font-mono text-xs">
                    curl -H "Authorization: Bearer &lt;token&gt;" http://localhost:5000/api/endpoint
                  </div>
                </div>
              </div>
            </div>

            {/* API Test Section */}
            <div>
              <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-3">Test API Authentication</h3>
              <div className="bg-green-50 dark:bg-green-950 rounded-lg p-4 border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-700 dark:text-green-400 mb-3">
                  Test if your token can authenticate against the approvn-api:
                </p>
                <div className="flex flex-col gap-2">
                  <Button 
                    onClick={testApiCall} 
                    disabled={!accessToken || apiTest.loading}
                    className="bg-green-600 hover:bg-green-700 w-fit"
                  >
                    {apiTest.loading ? 'Testing...' : 'Test API Health Endpoint'}
                  </Button>
                  {apiTest.result && (
                    <div className="text-green-600 dark:text-green-400 text-sm p-2 bg-green-100 dark:bg-green-900 rounded">
                      {apiTest.result}
                    </div>
                  )}
                  {apiTest.error && (
                    <div className="text-red-600 dark:text-red-400 text-sm p-2 bg-red-100 dark:bg-red-900 rounded">
                      {apiTest.error}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
